"use client"

import Link from "next/link"
import { useState, useRef } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { ArrowLeft, AlertCircle } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { useToast } from "@/hooks/use-toast"

// Definir interface para erros de autenticação
interface AuthError {
  message?: string;
  code?: string;
  statusCode?: number;
  error?: any;
  data?: any;
}

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const router = useRouter()
  const { signIn } = useAuth()
  const { toast } = useToast()

  // Função auxiliar para mapear erros do Supabase para mensagens amigáveis
  const getErrorMessage = (error: any): string => {
    // Log do erro completo para debug
    console.log("Erro bruto recebido:", error);
    
    if (!error) {
      return "Ocorreu um erro ao tentar fazer login. Tente novamente."
    }
    
    // Tratamento específico para AuthApiError do Supabase
    if (error.__isAuthError) {
      console.log("Detectado AuthApiError", error);
      
      // Verificar o código de erro
      if (error.code === "invalid_credentials") {
        return "Email ou senha incorretos. Por favor, verifique suas credenciais."
      }
      
      // Outros códigos de erro do AuthApiError
      switch (error.code) {
        case "email_not_confirmed":
          return "Email não confirmado. Por favor, verifique sua caixa de entrada e clique no link de confirmação."
        case "user_not_found":
          return "Usuário não encontrado. Verifique seu email ou registre-se para criar uma nova conta."
        case "invalid_email":
          return "Email inválido. Por favor, verifique se o endereço de email está correto."
        case "too_many_requests":
        case "rate_limit_exceeded":
          return "Muitas tentativas de login. Por favor, tente novamente mais tarde."
      }
    }
    
    // Se o erro for um objeto com uma propriedade error, use-a
    if (error.error) {
      console.log("Erro dentro de error:", error.error);
      error = error.error;
    }
    
    // Se o erro for um objeto com propriedade data, use-a
    if (error.data) {
      console.log("Erro dentro de data:", error.data);
      error = error.data;
    }
    
    // Verificar primeiro pelo código do erro
    if (error.code) {
      console.log("Erro tem código:", error.code);
      const errorCode = typeof error.code === 'string' ? error.code.toLowerCase() : error.code.toString();
      
      switch (errorCode) {
        case "invalid_credentials":
          return "Email ou senha incorretos. Por favor, verifique suas credenciais."
        case "email_not_confirmed":
          return "Email não confirmado. Por favor, verifique sua caixa de entrada e clique no link de confirmação."
        case "user_not_found":
          return "Usuário não encontrado. Verifique seu email ou registre-se para criar uma nova conta."
        case "invalid_email":
          return "Email inválido. Por favor, verifique se o endereço de email está correto."
        case "too_many_requests":
        case "rate_limit_exceeded":
          return "Muitas tentativas de login. Por favor, tente novamente mais tarde."
      }
    }
    
    // Se não encontrou pelo código, verificar pela mensagem
    if (error.message) {
      console.log("Erro tem mensagem:", error.message);
      const errorMessage = typeof error.message === 'string' ? error.message.toLowerCase() : '';
      
      if (errorMessage.includes("invalid login credentials")) {
        return "Email ou senha incorretos. Por favor, verifique suas credenciais."
      } else if (errorMessage.includes("email not confirmed")) {
        return "Email não confirmado. Por favor, verifique sua caixa de entrada e clique no link de confirmação."
      } else if (errorMessage.includes("rate limit")) {
        return "Muitas tentativas de login. Por favor, tente novamente mais tarde."
      } else if (errorMessage.includes("user not found")) {
        return "Usuário não encontrado. Verifique seu email ou registre-se para criar uma nova conta."
      } else if (errorMessage.includes("invalid email")) {
        return "Email inválido. Por favor, verifique se o endereço de email está correto."
      } else if (errorMessage.includes("too many requests")) {
        return "Muitas solicitações. Por favor, tente novamente mais tarde."
      } else if (errorMessage.includes("network")) {
        return "Erro de conexão. Verifique sua conexão com a internet e tente novamente."
      }
      
      // Retorna a mensagem original se não tiver um mapeamento específico
      return error.message;
    }
    
    // Verificar por outros padrões comuns de erro
    if (typeof error === 'string') {
      const errorStr = error.toLowerCase();
      if (errorStr.includes("invalid") && errorStr.includes("credentials")) {
        return "Email ou senha incorretos. Por favor, verifique suas credenciais.";
      }
    }
    
    // Fallback para erro genérico
    return "Ocorreu um erro ao tentar fazer login. Tente novamente.";
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Limpar mensagem de erro ao tentar novamente
    setErrorMessage(null)
    
    const formData = new FormData(e.target as HTMLFormElement)
    const email = formData.get('email') as string
    const password = formData.get('password') as string
    
    if (!email || !password) {
      setErrorMessage("Por favor, preencha todos os campos.")
      return
    }
    
    setIsLoading(true)
    
    try {
      console.log("Tentando fazer login com:", { email });
      const response = await signIn(email, password);
      console.log("Resposta do signIn:", response);
      
      // Verifica se a resposta é um erro (pode ser o erro direto ou estar dentro de .error)
      if (response && typeof response === 'object' && 
          // Usar verificação segura para a propriedade __isAuthError
          ('__isAuthError' in response && response.__isAuthError === true)) {
        // O erro está na raiz da resposta
        console.log("Erro de autenticação detectado na raiz:", response);
        const errorMsg = getErrorMessage(response);
        console.log("Mensagem de erro:", errorMsg);
        
        // Mostrar erro na interface
        setErrorMessage(errorMsg);
        setIsLoading(false);
        return;
        
      } else if (response && response.error) {
        // Verificar se o erro está dentro da propriedade error
        if (typeof response.error === 'object' && response.error !== null && 
            ('__isAuthError' in response.error && response.error.__isAuthError === true)) {
          console.log("Erro de autenticação detectado dentro de error:", response.error);
        } else {
          console.log("Erro genérico na resposta:", response.error);
        }
        
        const errorMsg = getErrorMessage(response.error);
        console.log("Mensagem de erro:", errorMsg);
        
        // Mostrar erro na interface
        setErrorMessage(errorMsg);
        setIsLoading(false);
        return;
        
      } else {
        // Sucesso: mostrar toast e redirecionar
        toast({
          title: "Login realizado com sucesso!",
          description: "Você está sendo redirecionado.",
          duration: 3000
        })
        
        // Redirecionar após um breve intervalo
        setTimeout(() => {
          router.push("/admin");
        }, 500);
      }
    } catch (error: unknown) {
      console.log("Erro capturado no catch:", error);
      const errorMsg = getErrorMessage(error);
      console.log("Mensagem de erro no catch:", errorMsg);
      
      // Mostrar erro na interface
      setErrorMessage(errorMsg);
    } finally {
      // Desativar o carregamento se ainda estiver ativo
      if (isLoading) {
        setIsLoading(false)
      }
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12">
      <Card className="w-full max-w-md">
        <div className="absolute left-4 top-4">
          <Button variant="ghost" size="icon" onClick={() => window.history.back()} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </div>
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">Login</CardTitle>
          <CardDescription>Entre com seu email e senha para acessar sua conta</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form ref={formRef} onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                name="email"
                type="email" 
                placeholder="seu@email.com" 
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Senha</Label>
                <Link href="/auth/forgot-password" className="text-xs text-primary hover:underline">
                  Esqueceu a senha?
                </Link>
              </div>
              <Input 
                id="password" 
                name="password"
                type="password" 
                placeholder="••••••••" 
                required
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="remember" />
              <Label htmlFor="remember" className="text-sm">
                Lembrar de mim
              </Label>
            </div>
            <Button 
              className="w-full bg-black hover:bg-gray-800" 
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Entrando..." : "Entrar"}
            </Button>
            
            {/* Mensagem de erro abaixo do botão */}
            {errorMessage && (
              <div className="mt-3 flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
                <AlertCircle className="h-4 w-4" />
                <span>{errorMessage}</span>
              </div>
            )}
          </form>

          <div className="hidden relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t"></span>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Ou continue com</span>
            </div>
          </div>

          <div className="hidden grid grid-cols-2 gap-4">
            <Button variant="outline" type="button" onClick={() => toast({ title: "Recurso não disponível", description: "Login com Google será implementado em breve." })}>
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </Button>
            <Button variant="outline" type="button" onClick={() => toast({ title: "Recurso não disponível", description: "Login com Facebook será implementado em breve." })}>
              <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
              </svg>
              Facebook
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex justify-center">
          <div className="text-sm text-muted-foreground">
            Não tem uma conta?{" "}
            <Link href="/auth/register" className="text-primary hover:underline">
              Registre-se
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}
